// src/Components/modules/Coordinacion/MovilesColumn.js

import React from "react";
import PropTypes from "prop-types";
import {
  Paper,
  Typography,
  Box,
  TextField,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Chip,
  Divider,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SimpleBarReact from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import PersonIcon from "@mui/icons-material/Person";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SearchIcon from "@mui/icons-material/Search";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import BlockIcon from "@mui/icons-material/Block";

const MovilesColumn = ({
  moviles,
  searchTerm,
  setSearchTerm,
  selectedMovilId,
  onSelectMovil,
  selectedEstadoVehiculo,
  onEstadoVehiculoChange,
}) => {
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
  };

  // Filtrar móviles por estado de vehículo
  const filteredByEstado = selectedEstadoVehiculo && selectedEstadoVehiculo !== "todos"
    ? moviles.filter((m) => m.estado === selectedEstadoVehiculo)
    : moviles;

  // Función para verificar documentos del vehículo próximos a vencer o vencidos
  const getDocumentosAlerta = (movil, diasAlerta = 7) => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const fechaLimite = new Date(hoy);
    fechaLimite.setDate(fechaLimite.getDate() + diasAlerta);

    const alertas = [];
    const documentosVehiculo = [
      { campo: "fecha_vencimiento_soat", nombre: "SOAT" },
      { campo: "fecha_vencimiento_tecnomecanica", nombre: "Tecnomecánica" },
      { campo: "fecha_vencimiento_tarjeta_operacion", nombre: "T. Operación" },
      { campo: "fecha_vencimiento_contractual", nombre: "P. Contractual" },
      { campo: "fecha_vencimiento_extracontractual", nombre: "P. Extracontractual" },
      { campo: "fecha_vencimiento_preventiva", nombre: "Preventiva" },
    ];

    // Verificar documentos del vehículo
    documentosVehiculo.forEach(({ campo, nombre }) => {
      if (movil[campo]) {
        const fechaVenc = new Date(movil[campo]);
        fechaVenc.setHours(0, 0, 0, 0);
        if (fechaVenc < hoy) {
          alertas.push({ documento: nombre, tipo: "vencido", fecha: movil[campo] });
        } else if (fechaVenc <= fechaLimite) {
          const diasRestantes = Math.ceil((fechaVenc - hoy) / (1000 * 60 * 60 * 24));
          alertas.push({ documento: nombre, tipo: "proximo", dias: diasRestantes, fecha: movil[campo] });
        }
      }
    });

    // Verificar licencia del conductor
    if (movil.Conductor?.fecha_vencimiento_licencia) {
      const fechaLicencia = new Date(movil.Conductor.fecha_vencimiento_licencia);
      fechaLicencia.setHours(0, 0, 0, 0);
      if (fechaLicencia < hoy) {
        alertas.push({ documento: "Licencia Conductor", tipo: "vencido", fecha: movil.Conductor.fecha_vencimiento_licencia });
      } else if (fechaLicencia <= fechaLimite) {
        const diasRestantes = Math.ceil((fechaLicencia - hoy) / (1000 * 60 * 60 * 24));
        alertas.push({ documento: "Licencia Conductor", tipo: "proximo", dias: diasRestantes, fecha: movil.Conductor.fecha_vencimiento_licencia });
      }
    }

    return alertas;
  };

  // Función para verificar si el móvil está deshabilitado para asignación
  const getMovilDisabled = (movil) => {
    // Deshabilitado si el vehículo no está activo
    if (movil.estado !== "Activo") return true;
    // Deshabilitado si no hay conductor
    if (!movil.Conductor) return true;
    // Deshabilitado si el conductor está suspendido/inactivo
    if (
      movil.Conductor?.estado === "Suspendido" ||
      movil.Conductor?.estado === "Inactivo"
    )
      return true;
    return false;
  };

  // Función para obtener el color de fondo
  const getBackgroundColor = (movil) => {
    if (selectedMovilId === movil.id)
      return "linear-gradient(145deg, #e0f7e0, #c5f7c5)";
    // Vehículo no activo - fondo gris
    if (movil.estado !== "Activo")
      return "linear-gradient(145deg, #f5f5f5, #e0e0e0)";
    if (movil.Conductor && !getMovilDisabled(movil))
      return "linear-gradient(145deg, #e3f2fd, #bbdefb)";
    return "linear-gradient(145deg, #ffebee, #ffcdd2)";
  };

  const handleDragStart = (e, movil) => {
    // Evitar arrastrar móviles no activos, sin conductor o con conductores inactivos/suspendidos
    if (getMovilDisabled(movil)) {
      e.preventDefault();
      return false;
    }

    e.dataTransfer.setData("movil", JSON.stringify(movil));
  };

  // Obtener el estado y color del chip para el móvil
  const getMovilStatus = (movil) => {
    // Primero verificar estado del vehículo
    if (movil.estado !== "Activo") {
      const vehicleStatusMap = {
        'Inactivo': { label: "Vehículo Inactivo", color: "warning", icon: <BlockIcon fontSize="small" /> },
        'Suspendido': { label: "Vehículo Suspendido", color: "error", icon: <CancelIcon fontSize="small" /> },
        'Retirado': { label: "Vehículo Retirado", color: "default", icon: <CancelIcon fontSize="small" /> },
      };
      return vehicleStatusMap[movil.estado] || { label: movil.estado, color: "default", icon: <BlockIcon fontSize="small" /> };
    }

    // Luego verificar conductor
    if (!movil.Conductor) {
      return {
        label: "Sin Conductor",
        color: "error",
        icon: <ErrorOutlineIcon fontSize="small" />,
      };
    }

    if (movil.Conductor.estado === "Suspendido") {
      return {
        label: "Conductor Suspendido",
        color: "error",
        icon: <CancelIcon fontSize="small" />,
      };
    }

    if (movil.Conductor.estado === "Inactivo") {
      return {
        label: "Conductor Inactivo",
        color: "warning",
        icon: <CancelIcon fontSize="small" />,
      };
    }

    return {
      label: "Activo",
      color: "success",
      icon: <CheckCircleIcon fontSize="small" />,
    };
  };

  return (
    <Paper
      elevation={2}
      sx={{
        padding: 0,
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        maxWidth: "100%",
      }}
    >
      <Box
        sx={{
          backgroundColor: "#0a2e5c",
          padding: "16px",
          color: "white",
          position: "sticky",
          top: 0,
          zIndex: 1000,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <DirectionsCarIcon /> Móviles
        </Typography>
      </Box>

      <Box sx={{ padding: "16px", backgroundColor: "#f8f9fa" }}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Buscar móvil..."
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{
            backgroundColor: "#ffffff",
            borderRadius: "8px",
            marginBottom: "12px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              "& fieldset": {
                borderColor: "#e0e0e0",
              },
              "&:hover fieldset": {
                borderColor: "#1a73e8",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#1a73e8",
              },
            },
          }}
        />
        <FormControl fullWidth size="small">
          <Select
            value={selectedEstadoVehiculo}
            onChange={(e) => onEstadoVehiculoChange(e.target.value)}
            sx={{
              backgroundColor: "#ffffff",
              borderRadius: "8px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#e0e0e0",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#1a73e8",
              },
            }}
          >
            <MenuItem value="Activo">Vehículos Activos</MenuItem>
            <MenuItem value="Inactivo">Vehículos Inactivos</MenuItem>
            <MenuItem value="Suspendido">Vehículos Suspendidos</MenuItem>
            <MenuItem value="Retirado">Vehículos Retirados</MenuItem>
            <MenuItem value="todos">Todos los Estados</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Divider />

      <Box sx={{ padding: "8px 16px" }}>
        <Typography variant="subtitle2" color="textSecondary">
          {filteredByEstado.length} móviles encontrados
        </Typography>
      </Box>

      <SimpleBarReact
        style={{
          height: "calc(100vh - 290px)",
          minHeight: "520px",
          overflowY: "auto",
          width: "100%",
        }}
      >
        <List
          sx={{
            padding: "8px 12px",
            boxSizing: "border-box",
            width: "100%",
          }}
        >
          {filteredByEstado.map((movil) => {
            const movilStatus = getMovilStatus(movil);
            const isDisabled = getMovilDisabled(movil);
            const alertasDocumentos = getDocumentosAlerta(movil);
            const tieneVencidos = alertasDocumentos.some((a) => a.tipo === "vencido");
            const tieneProximos = alertasDocumentos.some((a) => a.tipo === "proximo");
            return (
              <ListItem
                key={movil.id || movil.codigo}
                draggable={!isDisabled}
                aria-disabled={isDisabled}
                onDragStart={(e) => handleDragStart(e, movil)}
                onClick={() => !isDisabled && onSelectMovil(movil)}
                sx={{
                  background: getBackgroundColor(movil),
                  marginBottom: "12px",
                  borderRadius: "12px",
                  padding: "10px",
                  border:
                    selectedMovilId === movil.id
                      ? "2px solid #2e7d32"
                      : tieneVencidos
                      ? "2px solid #d32f2f"
                      : tieneProximos
                      ? "2px solid #ed6c02"
                      : "1px solid rgba(0,0,0,0.12)",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                  "&:hover": {
                    cursor: !isDisabled ? "grab" : "not-allowed",
                  },
                  opacity: isDisabled ? 0.6 : 1,
                  position: "relative",
                  width: "calc(100% - 8px)",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  mx: "auto",
                }}
              >
                <ListItemText
                  primary={
                    <Box
                      component="div"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "6px",
                        width: "100%",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: "bold",
                            color: "#1a202c",
                            wordBreak: "break-word",
                            fontSize: "1rem",
                          }}
                        >
                          {movil.codigo}
                        </Typography>
                        {alertasDocumentos.length > 0 && (
                          <Tooltip
                            title={
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>
                                  {tieneVencidos ? "⚠️ Documentos con problemas:" : "⏰ Próximos a vencer:"}
                                </Typography>
                                {alertasDocumentos.map((alerta, idx) => (
                                  <Typography key={idx} variant="body2" sx={{ fontSize: "0.75rem" }}>
                                    • {alerta.documento}:{" "}
                                    {alerta.tipo === "vencido"
                                      ? `Vencido (${new Date(alerta.fecha).toLocaleDateString("es-CO")})`
                                      : `Vence en ${alerta.dias} día(s)`}
                                  </Typography>
                                ))}
                              </Box>
                            }
                            arrow
                            placement="top"
                          >
                            <WarningAmberIcon
                              sx={{
                                fontSize: "18px",
                                color: tieneVencidos ? "#d32f2f" : "#ed6c02",
                                cursor: "help",
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                      <Chip
                        size="small"
                        label={movilStatus.label}
                        color={movilStatus.color}
                        icon={movilStatus.icon}
                        sx={{
                          height: "24px",
                          "& .MuiChip-label": {
                            paddingLeft: "4px",
                            paddingRight: "4px",
                          },
                          "& .MuiChip-icon": {
                            marginLeft: "2px",
                          },
                        }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box component="div" sx={{ width: "100%" }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "6px",
                          color: "#718096",
                        }}
                      >
                        <DirectionsCarIcon
                          fontSize="small"
                          sx={{
                            marginRight: "8px",
                            color: "#4a5568",
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                          Placa: <strong>{movil.placa}</strong>
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "6px",
                        }}
                      >
                        <Avatar
                          sx={{
                            backgroundColor: movil.Conductor
                              ? "#3182ce"
                              : "#e53e3e",
                            width: 24,
                            height: 24,
                            marginRight: "8px",
                            flexShrink: 0,
                          }}
                        >
                          {movil.Conductor ? (
                            <PersonIcon sx={{ fontSize: "16px" }} />
                          ) : (
                            <ErrorOutlineIcon sx={{ fontSize: "16px" }} />
                          )}
                        </Avatar>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#4a5568",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "calc(100% - 32px)",
                          }}
                        >
                          {movil.Conductor?.nombre ?? "Sin conductor asignado"}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Avatar
                          sx={{
                            backgroundColor: movil.zona ? "#38a169" : "#e53e3e",
                            width: 24,
                            height: 24,
                            marginRight: "8px",
                            flexShrink: 0,
                          }}
                        >
                          <LocationOnIcon sx={{ fontSize: "16px" }} />
                        </Avatar>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#4a5568",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "calc(100% - 32px)",
                          }}
                        >
                          {movil.zona?.nombre ?? "Sin zona asignada"}
                        </Typography>
                      </Box>
                    </Box>
                  }
                  primaryTypographyProps={{ component: "div" }}
                  secondaryTypographyProps={{ component: "div" }}
                  sx={{ margin: 0, padding: 0 }}
                />
              </ListItem>
            );
          })}
        </List>
      </SimpleBarReact>
    </Paper>
  );
};

MovilesColumn.propTypes = {
  moviles: PropTypes.arrayOf(PropTypes.object).isRequired,
  searchTerm: PropTypes.string.isRequired,
  setSearchTerm: PropTypes.func.isRequired,
  selectedMovilId: PropTypes.number,
  onSelectMovil: PropTypes.func.isRequired,
  selectedEstadoVehiculo: PropTypes.string,
  onEstadoVehiculoChange: PropTypes.func.isRequired,
};

export default MovilesColumn;
